import type { InsightsSavedView, SavedViewFilters } from '#services/api/saved-views.ts';
import type { ProviderFilter } from '#utils/provider-filter.ts';
import type { HomeTab } from '../Home.utils.ts';

import * as React from 'react';
import { useSearchParams } from 'react-router';

import { allProvidersToken, displayedProvidersToken } from '#utils/provider-filter.ts';
import {
  applyFiltersToSearchParams,
  areFiltersEqual,
  getParsedSearchParams,
  normalizeFilters,
  serializeFilters,
} from '../Home.utils.ts';
import { isDisplayedProvidersFilter, setProviderFilterSearchParams } from './Provider.utils.ts';

type SelectedViewState = { mode: 'route' } | { mode: 'none' } | { mode: 'view'; viewId: number };

const FiltersContext = React.createContext<{
  /** Applies filter overrides to the current URL state. */
  onChange: (overrides: SavedViewFilters) => void;
  /** Remembers the last saved view involved in the current session. */
  lastActiveViewId: number | undefined;
  /** Tracks whether selection comes from the route, a local view, or neither. */
  selectedViewState: SelectedViewState;
  /** Marks a saved view as the current local selection. */
  selectView: (viewId: number) => void;
  /** Restores route-driven saved view state after closing a drilldown. */
  restoreRouteStateFromDrilldown: () => void;
} | null>(null);

export function FiltersProvider(props: {
  activeView: InsightsSavedView | undefined;
  providerIds: ProviderFilter;
  tabType: HomeTab;
  children: React.ReactNode;
}) {
  const [, setSearchParams] = useSearchParams();
  const [lastActiveViewId, setLastActiveViewId] = React.useState(props.activeView?.id);
  const [selectedViewState, setSelectedViewState] = React.useState<SelectedViewState>({
    mode: 'route',
  });

  const value = React.useMemo(
    () => ({
      onChange: (overrides: SavedViewFilters) => {
        setSearchParams((prev) => {
          const hasDisplayedProvidersFilter = !props.activeView && isDisplayedProvidersFilter(prev);
          const parsed = getParsedSearchParams(prev);
          const base =
            props.activeView?.filters ??
            serializeFilters({
              parsed: hasDisplayedProvidersFilter
                ? { ...parsed, providerIds: props.providerIds }
                : parsed,
              tabType: props.tabType,
            });
          const normalizedBase = normalizeFilters(base);
          const normalizedFilters = normalizeFilters({ ...base, ...overrides });

          if (areFiltersEqual(normalizedFilters, normalizedBase)) {
            return prev;
          }

          const explicitFilters =
            props.activeView && normalizedFilters.providerIds === undefined
              ? { ...normalizedFilters, providerIds: allProvidersToken }
              : normalizedFilters;
          const shouldPreserveDisplayedProviders =
            hasDisplayedProvidersFilter && !('providerIds' in overrides);
          const filtersToApply = { ...explicitFilters };
          if (shouldPreserveDisplayedProviders) {
            delete filtersToApply.providerIds;
          }
          const next = applyFiltersToSearchParams(filtersToApply, prev);

          if (shouldPreserveDisplayedProviders) {
            setProviderFilterSearchParams(next, displayedProvidersToken);
          }

          if (props.activeView) {
            setLastActiveViewId(props.activeView.id);
            setSelectedViewState({ mode: 'none' });
            next.delete('view');
            next.set('tab', props.activeView.tabType);
          }

          return next;
        });
      },
      lastActiveViewId,
      selectedViewState,
      selectView: (viewId: number) => {
        setLastActiveViewId(viewId);
        setSelectedViewState({ mode: 'view', viewId });
      },
      restoreRouteStateFromDrilldown: () => {
        if (!props.activeView) {
          setLastActiveViewId(undefined);
          setSelectedViewState({ mode: 'none' });
          return;
        }

        setLastActiveViewId(props.activeView.id);
        setSelectedViewState({ mode: 'route' });
      },
    }),
    [
      props.activeView,
      props.providerIds,
      props.tabType,
      setSearchParams,
      lastActiveViewId,
      selectedViewState,
    ]
  );

  return <FiltersContext value={value}>{props.children}</FiltersContext>;
}

// This file isn't touched that often so the rule is disabled to avoid having to create yet another
// file just for this function.
// oxlint-disable-next-line react/only-export-components
export function useFilters() {
  const value = React.use(FiltersContext);
  if (!value) throw new Error('`useFilters` must be called within `FiltersProvider`');
  return value;
}
